/**
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 */

import { transform } from 'ol/proj';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import type { CoreAppStore } from '@opengeoweb/store';
import {
  genericSelectors,
  genericActions,
  mapSelectors,
  syncGroupsActions,
  mapActions,
} from '@opengeoweb/store';
import type { MapLocation } from '@opengeoweb/webmap-react';
import FlySafePanel from './FlySafePanel';
import { useAutoUpdateAnimationWindow } from '../hooks/useAutoUpdateAnimationWindow';
import { parseShareParams, cleanShareParams } from '../utils/shareParams';
import {
  DAY_MS,
  roundToNearest5Min,
  SIX_HOURS_MS,
  WEEK_MS,
} from '../utils/timeUtils';
import type { Mode } from '../utils/types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function selectMapSlice(state: CoreAppStore): any {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stat = state as any;
  if (stat.map?.byId) {
    return stat.map;
  }
  if (stat.mapReducer?.byId) {
    return stat.mapReducer;
  }
  if (stat.webmap?.byId) {
    return stat.webmap;
  }
  if (stat.webMap?.byId) {
    return stat.webMap;
  }
  if (stat.maps?.byId) {
    return stat.maps;
  }
  return undefined;
}

function selectLinkedMapsSafe(state: CoreAppStore, panelId: string): string[] {
  const linkedMaps = genericSelectors.getLinkedMaps?.(state, panelId);
  if (Array.isArray(linkedMaps)) {
    return linkedMaps as string[];
  }
  return [];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getSyncGroupStoreSafe(state: CoreAppStore): any {
  const gs = genericSelectors;
  return gs.getSynchronizationGroupStore?.(state) ?? state;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getTimeSafe(syncStore: any, mapId: string): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const gs = genericSelectors as any;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return (
    gs.getTime?.(syncStore, mapId) ?? gs.selectTime?.(syncStore, mapId) ?? null
  );
}

interface FlySafeConnectProps {
  initialMode?: Mode;
  panelId: string;
  defaultLayerId: string;
  onSelectedStationsChange?: (ids: string[]) => void;
}

const FlySafeConnect: React.FC<FlySafeConnectProps> = ({
  initialMode = 'density',
  panelId,
  defaultLayerId,
  onSelectedStationsChange,
}) => {
  const dispatch = useDispatch();

  // Find linked maps and primary map
  const linkedMapIds: string[] = useSelector(
    (state: CoreAppStore) => selectLinkedMapsSafe(state, panelId),
    shallowEqual,
  );
  const primaryMapId = linkedMapIds[0] ?? null;

  // Parse share params once on mount
  const shareParams = useMemo(() => parseShareParams(), []);

  // Map pin visibility
  useEffect(() => {
    linkedMapIds.forEach((mapId) => {
      dispatch(mapActions.setDisableMapPin({ mapId, disableMapPin: false }));
      dispatch(
        mapActions.toggleMapPinIsVisible({ mapId, displayMapPin: true }),
      );
    });
  }, [dispatch, linkedMapIds]);

  // Read current time from sync-group store
  const currentTimeIso = useSelector((state: CoreAppStore) => {
    if (!primaryMapId) {
      return null;
    }
    const syncStore = getSyncGroupStoreSafe(state);
    return getTimeSafe(syncStore, primaryMapId);
  });

  // Read animation window (the animated range, not the full slider)
  // getAnimationStartTime / getAnimationEndTime return ISO strings
  const animationWindow = useSelector(
    (state: CoreAppStore): [Date, Date] | null => {
      if (!primaryMapId) {
        return null;
      }
      const startIso = mapSelectors.getAnimationStartTime(state, primaryMapId);
      const endIso = mapSelectors.getAnimationEndTime(state, primaryMapId);
      if (!startIso || !endIso) {
        return null;
      }
      const startMs = new Date(startIso).getTime();
      const endMs = new Date(endIso).getTime();
      if (isNaN(startMs) || isNaN(endMs)) {
        return null;
      }
      // Cap to at most one week
      const cappedStart = Math.max(startMs, endMs - WEEK_MS);
      return [new Date(cappedStart), new Date(endMs)];
    },
    // eslint-disable-next-line id-length
    (a, b) =>
      a === b ||
      (a != null &&
        a[0].getTime() === b?.[0].getTime() &&
        a[1].getTime() === b[1].getTime()),
  );

  // Selected time on the map (unix timestamp in seconds)
  const selectedMapTime = useSelector((state: CoreAppStore): Date | null => {
    if (!primaryMapId) {
      return null;
    }
    const ts = mapSelectors.getSelectedTime(state, primaryMapId);
    if (!ts) {
      return null;
    }
    // getSelectedTime returns seconds since epoch
    return new Date(ts * 1000);
  });

  // When switching maps or moving in the timeslider
  const prevMapTimeRef = useRef<string | null>(null);
  useEffect(() => {
    // Reset when switching primary map
    prevMapTimeRef.current = null;
  }, [primaryMapId]);

  useEffect(() => {
    if (!primaryMapId) {
      return;
    }
    if (prevMapTimeRef.current === currentTimeIso) {
      return;
    }
    prevMapTimeRef.current = currentTimeIso;
  }, [primaryMapId, currentTimeIso]);

  // Pins
  interface PinByMap {
    mapId: string;
    pin: MapLocation | null | undefined;
  }
  const pins: PinByMap[] = useSelector(
    (state: CoreAppStore) => {
      const slice = selectMapSlice(state);
      if (!slice?.byId || !linkedMapIds.length) {
        return [];
      }
      return linkedMapIds.map((mapId) => ({
        mapId,
        pin: slice.byId[mapId]?.mapPinLocation ?? null,
      }));
    },
    (prev, next) => {
      if (prev.length !== next.length) {
        return false;
      }
      return prev.every(
        (entry, idx) =>
          entry.mapId === next[idx].mapId && entry.pin === next[idx].pin,
      );
    },
  );

  const [latestPin, setLatestPin] = useState<{
    mapId: string;
    pin: MapLocation;
  } | null>(null);
  const prevKeyByMapRef = useRef<Record<string, string>>({});
  useEffect(() => {
    let changed: { mapId: string; pin: MapLocation } | null = null;
    pins.forEach(({ mapId, pin }) => {
      const key =
        pin == null
          ? ''
          : `${pin.lon ?? ''}${pin.lat ?? ''}${pin.projectionX ?? ''}${pin.projectionY ?? ''}${pin.srs ?? ''}`;
      if (prevKeyByMapRef.current[mapId] !== key) {
        prevKeyByMapRef.current[mapId] = key;
        if (pin) {
          changed = { mapId, pin };
        }
      }
    });
    if (changed) {
      setLatestPin(changed);
    }
  }, [pins]);

  // Local time window (used for data fetching)
  const [timeWindow, setTimeWindow] = useState<[Date, Date]>(() => {
    const now = new Date();
    return [new Date(now.getTime() - 60 * 60 * 1000), now];
  });

  // Avoid redundant "recenter" operations
  const lastRecenterCenterRef = useRef<number | null>(null);

  // Update time window only when it falls outside the current window
  // to prevent too much refetching
  useEffect(() => {
    if (!currentTimeIso) {
      return;
    }

    const mapTime = new Date(currentTimeIso);
    if (isNaN(mapTime.getTime())) {
      return;
    }

    const timeOfMap = mapTime.getTime();
    const [from, to] = timeWindow;
    const fromMs = from.getTime();
    const toMs = to.getTime();

    // If inside current window, do nothing
    if (timeOfMap >= fromMs && timeOfMap <= toMs) {
      return;
    }

    // If outside, recenter
    const snappedCenter = roundToNearest5Min(mapTime).getTime();
    if (lastRecenterCenterRef.current === snappedCenter) {
      return;
    }
    lastRecenterCenterRef.current = snappedCenter;

    const newFrom = new Date(snappedCenter - SIX_HOURS_MS);
    const newTo = new Date(snappedCenter + SIX_HOURS_MS);

    setTimeWindow((prev) => {
      if (
        prev[0].getTime() === newFrom.getTime() &&
        prev[1].getTime() === newTo.getTime()
      ) {
        return prev;
      }
      return [newFrom, newTo];
    });
  }, [currentTimeIso, timeWindow]);

  useEffect(() => {
    return (): void => {
      dispatch(syncGroupsActions.deleteSharedData({ panelId }));
    };
  }, [dispatch, panelId]);

  // Adjust map animation time step based on window duration
  useEffect(() => {
    if (!animationWindow || !primaryMapId) {
      return;
    }
    const durationMs =
      animationWindow[1].getTime() - animationWindow[0].getTime();
    const useCustomStep = durationMs > DAY_MS;
    const timeStep = useCustomStep ? 60 : 5;
    dispatch(
      mapActions.toggleTimestepAuto({
        mapId: primaryMapId,
        timestepAuto: !useCustomStep,
      }),
    );
    // When using a custom step, clear autoTimeStepLayerId so the animation
    // list falls back to interval-based generation (the layer's comma-separated
    // TIME dimension would otherwise override our interval).  Restore it for
    // the default 5-min case so auto-detection works normally.
    dispatch(
      mapActions.setAutoLayerId({
        mapId: primaryMapId,
        autoTimeStepLayerId: useCustomStep ? undefined : defaultLayerId,
      }),
    );
    dispatch(mapActions.setTimeStep({ mapId: primaryMapId, timeStep }));
  }, [animationWindow, primaryMapId, defaultLayerId, dispatch]);

  // Convert pin coords to lat lon
  const toLonLat = (
    pin: MapLocation,
  ): [number | null, number | null, string | undefined] => {
    let lon = pin.lon ?? null;
    let lat = pin.lat ?? null;
    if (
      (lon == null || lat == null) &&
      pin.projectionX != null &&
      pin.projectionY != null &&
      pin.srs
    ) {
      try {
        const [lon4326, lat4326] = transform(
          [pin.projectionX, pin.projectionY],
          pin.srs,
          'EPSG:4326',
        );
        if (lon == null) {
          lon = lon4326;
        }
        if (lat == null) {
          lat = lat4326;
        }
      } catch {
        // ignore
      }
    }
    return [lon, lat, pin.srs];
  };

  const dataRequest = useMemo(() => {
    if (!latestPin) {
      return null;
    }

    const [lon, lat] = toLonLat(latestPin.pin);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
      return null;
    }

    const lonNum = lon as number;
    const latNum = lat as number;

    const [from, to] = timeWindow;
    const datetime = `${from.toISOString()}/${to.toISOString()}`;

    return {
      lon: lonNum,
      lat: latNum,
      datetime,
      key: `${lonNum.toFixed(6)}:${latNum.toFixed(6)}:${from.getTime()}:${to.getTime()}`,
    };
  }, [latestPin, timeWindow]);

  const handleTimeSelect = useCallback(
    (time: Date) => {
      if (!primaryMapId) {
        return;
      }
      const iso = time.toISOString().replace(/\.\d{3}Z$/, 'Z');
      dispatch(
        genericActions.setTime({
          sourceId: primaryMapId,
          origin: 'chartInteraction',
          value: { currentTime: iso },
        }),
      );
    },
    [dispatch, primaryMapId],
  );

  const handleAnimationWindowChange = useCallback(
    (window: [Date, Date] | null, disableAutoUpdate = false) => {
      if (!primaryMapId) {
        return;
      }
      if (disableAutoUpdate) {
        dispatch(
          mapActions.toggleAutoUpdate({
            mapId: primaryMapId,
            shouldAutoUpdate: false,
          }),
        );
      }
      if (window) {
        dispatch(
          mapActions.setAnimationStartTime({
            mapId: primaryMapId,
            animationStartTime: window[0].toISOString(),
          }),
        );
        dispatch(
          mapActions.setAnimationEndTime({
            mapId: primaryMapId,
            animationEndTime: window[1].toISOString(),
          }),
        );
      }
    },
    [dispatch, primaryMapId],
  );
  const [forceRefetchState, setForceRefetchState] = useState<boolean>(true);
  const forceRefetch = (): void => {
    setForceRefetchState(!forceRefetchState);
  };

  // Read the auto-update toggle from the Redux store
  const isAutoUpdating = useSelector((state: CoreAppStore) =>
    primaryMapId ? mapSelectors.isAutoUpdating(state, primaryMapId) : false,
  );

  // Restore shared state from URL params
  useEffect(() => {
    if (!shareParams || !primaryMapId) {
      return;
    }

    // Restore animation window
    if (shareParams.animationStart && shareParams.animationEnd) {
      const start = new Date(shareParams.animationStart);
      const end = new Date(shareParams.animationEnd);
      if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
        handleAnimationWindowChange([start, end], true);
      }
      dispatch(
        genericActions.setTime({
          sourceId: primaryMapId,
          origin: 'shareParams',
          value: { currentTime: end.toISOString().replace(/\.\d{3}Z$/, 'Z') },
        }),
      );
    }

    cleanShareParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryMapId]);

  // Auto-update animation end time to latest available layer timestamp
  useAutoUpdateAnimationWindow(
    primaryMapId,
    defaultLayerId,
    handleAnimationWindowChange,
    forceRefetch,
    isAutoUpdating,
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <FlySafePanel
        animationWindow={animationWindow}
        dataRequest={dataRequest}
        defaultLayerId={defaultLayerId}
        forceRefetchState={forceRefetchState}
        initialMode={initialMode}
        mapTime={selectedMapTime}
        onAnimationWindowChange={handleAnimationWindowChange}
        onSelectedStationsChange={onSelectedStationsChange}
        onTimeSelect={handleTimeSelect}
        shareParams={shareParams}
      />
    </div>
  );
};

export default FlySafeConnect;
